import React from 'react';
import { PaylinkStatus } from 'skipify-types';
import {
  AddLink,
  Check,
  Close,
  CurrencyExchange,
  MoneyOff,
  Send,
  TimerOff,
} from '@mui/icons-material';
import { TransactionStatus } from '../../store/modules/orders/types';
import { PartiallyPaid } from '../../components/icons/PartiallyPaid';
import {
  colorDisabled,
  colorWarning,
  colorSuccess,
  colorError,
  StatusColor,
} from '../../util/constants';

const StatusUiMapper: Record<PaylinkStatus, { icon: JSX.Element; color: StatusColor }> = {
  CANCELLED: { icon: <Close />, color: colorDisabled },
  FAILED: { icon: <MoneyOff />, color: colorError },
  SENT: { icon: <Send />, color: colorWarning },
  OPENED: { icon: <Send />, color: colorWarning },
  PAID: { icon: <Check />, color: colorSuccess },
  EXPIRED: { icon: <TimerOff />, color: colorDisabled },
  CREATED: { icon: <AddLink />, color: colorDisabled },
  PARTIALLY_REFUNDED: { icon: <CurrencyExchange />, color: colorSuccess },
  PARTIALLY_PAID: { icon: <PartiallyPaid />, color: colorSuccess },
  REFUNDED: { icon: <CurrencyExchange />, color: colorSuccess },
  RETURNED: { icon: <MoneyOff />, color: colorError },
};

const hiddenIcon = <Close sx={{ fontSize: '0 !important' }} />;
export const getStatusUi = (status: PaylinkStatus) => {
  return (
    StatusUiMapper[status] ?? {
      icon: hiddenIcon,
      color: colorDisabled,
    }
  );
};

export enum PaylinkActions {
  EDIT,
  RESEND,
  CANCEL,
  REFUND,
  VIEW_TRANSACTIONS,
}

const ActionsAllowedByStatus: Record<PaylinkStatus, PaylinkActions[]> = {
  CANCELLED: [],
  FAILED: [PaylinkActions.RESEND],
  SENT: [PaylinkActions.EDIT, PaylinkActions.RESEND, PaylinkActions.CANCEL],
  OPENED: [PaylinkActions.EDIT, PaylinkActions.RESEND, PaylinkActions.CANCEL],
  PAID: [PaylinkActions.REFUND, PaylinkActions.VIEW_TRANSACTIONS],
  PARTIALLY_PAID: [PaylinkActions.REFUND, PaylinkActions.VIEW_TRANSACTIONS],
  EXPIRED: [],
  CREATED: [PaylinkActions.EDIT, PaylinkActions.RESEND, PaylinkActions.CANCEL],
  PARTIALLY_REFUNDED: [PaylinkActions.REFUND, PaylinkActions.VIEW_TRANSACTIONS],
  REFUNDED: [PaylinkActions.VIEW_TRANSACTIONS],
  RETURNED: [PaylinkActions.VIEW_TRANSACTIONS],
};

export const getAllowedActions = (status: PaylinkStatus) => ActionsAllowedByStatus[status] ?? [];

const StatusLabelMapper: Record<PaylinkStatus, string> = {
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
  SENT: 'Sent',
  OPENED: 'Opened',
  PAID: 'Paid',
  PARTIALLY_PAID: 'Partially Paid',
  EXPIRED: 'Expired',
  CREATED: 'Created',
  PARTIALLY_REFUNDED: 'Partially Refunded',
  REFUNDED: 'Fully Refunded',
  RETURNED: 'ACH Returned',
};
export const getStatusLabel = (status: PaylinkStatus) => {
  return StatusLabelMapper[status] ?? 'Unknown';
};

const TransactionStatusLabelMapper: Record<TransactionStatus, string> = {
  Failed: 'Failed',
  Unknown: 'Unknown',
  Captured: 'Captured',
  Authorized: 'Authorized',
  Reversed: 'Reversed',
  Cancelled: 'Cancelled',
  Refunded: 'Refunded',
  PartialCaptured: 'Partially Captured',
  PartialRefunded: 'Partially Refunded',
  Returned: 'ACH Returned',
};
export const getTransactionStatusLabel = (status: TransactionStatus) => {
  return TransactionStatusLabelMapper[status] ?? 'Unknown';
};

const TransactionStatusColorMapper: Record<TransactionStatus, StatusColor> = {
  Failed: colorError,
  Unknown: colorDisabled,
  Captured: colorSuccess,
  Authorized: colorSuccess,
  Reversed: colorWarning,
  Cancelled: colorError,
  Refunded: colorSuccess,
  PartialCaptured: colorDisabled,
  PartialRefunded: colorSuccess,
  Returned: colorError,
};
export const getTransactionStatusUi = (status: TransactionStatus) => {
  return {
    icon: hiddenIcon,
    color: TransactionStatusColorMapper[status] ?? colorDisabled,
  };
};

export const ATTACHMENT_VARS = {
  MAX_FILE_SIZE: 2 * 1024 * 1024, // 2 MB
  QUERY_CACHE_TTL: 1800, // 30 minutes
  ACCEPTED_FILE_TYPES: [
    'application/pdf',
    'image/gif',
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
  ],
  ACCEPTED_FILE_TYPES_ATTR: 'image/*,.heic,.heif,.pdf',
};
